import { z } from 'zod';

// AWS Region options (Studio-style short names)
export const awsRegionOptions = [
  { value: 'NA1', label: 'NA1' },
  { value: 'NA2', label: 'NA2' },
  { value: 'CA', label: 'CA' },
  { value: 'EU', label: 'EU' },
] as const;

// Project status options
export const projectStatusOptions = [
  { value: 'ALL', label: 'ALL' },
  { value: 'ROLLOUT', label: 'ROLLOUT' },
  { value: 'POC', label: 'POC' },
  { value: 'INTERNAL', label: 'INTERNAL' },
  { value: 'DEMO', label: 'DEMO' },
] as const;
const projectStatusValues = projectStatusOptions.map(opt => opt.value) as [string, ...string[]];
export const projectTypeValues = projectStatusOptions
  .filter(opt => opt.value !== 'ALL')
  .map(opt => opt.value) as [string, ...string[]];

// Mock utilities for existing utilities
export type MockUtility = {
  id: string;
  name: string;
  address?: string;
  state: string;
  zipCode: string;
};

export const mockUtilities: MockUtility[] = [
  { id: 'util_1', name: 'Pacific Gas & Electric', address: '77 Beale St', state: 'CA', zipCode: '94105' },
  { id: 'util_2', name: 'Southern California Edison', address: '2244 Walnut Grove Ave', state: 'CA', zipCode: '91770' },
  { id: 'util_3', name: 'Consolidated Edison', address: '4 Irving Pl', state: 'NY', zipCode: '10003' },
  { id: 'util_4', name: 'BC Hydro', address: '333 Dunsmuir St', state: 'BC', zipCode: 'V6B 5R3' },
  { id: 'util_5', name: 'Hydro Quebec', address: '75 René-Lévesque Blvd W', state: 'QC', zipCode: 'H2Z 1A4' },
  { id: 'util_6', name: 'Enel', address: 'Viale Regina Margherita, 137', state: 'RM', zipCode: '00198' },
  { id: 'util_7', name: 'Acea', address: 'Piazzale Ostiense, 2', state: 'RM', zipCode: '00154' },
  { id: 'util_8', name: 'British Gas', address: 'Millstream, Maidenhead Road', state: 'Berkshire', zipCode: 'SL4 5GD' },
  { id: 'util_9', name: 'Origin Energy', address: 'Level 4, 321 Exhibition St', state: 'VIC', zipCode: '3000' },
];

// Project user type options
export const projectUserTypeOptions = [
  { value: 'RESI', label: 'Residential' },
  { value: 'SMB', label: 'Small-Medium Business' },
] as const;
const projectUserTypeValues = projectUserTypeOptions.map(opt => opt.value) as [string, ...string[]];

// Measurement type options
export const measurementTypeOptions = [
  { id: 'Electric', label: 'Electric' },
  { id: 'Gas', label: 'Gas' },
  { id: 'Water', label: 'Water' },
] as const;
const measurementTypeValues = measurementTypeOptions.map(opt => opt.id) as [string, ...string[]];

// Meter type options
export const electricMeterOptions = ['AMI', 'AMR', 'NSM'] as const;
export const gasMeterOptions = ['AMI', 'AMR', 'NSM'] as const;
export const waterMeterOptions = ['AMI', 'AMR'] as const;

// Unit options
export const electricUnitOptions = ['kWh', 'kW', 'W', 'Wh'] as const;
export const gasUnitOptions = ['CCF', 'Therms'] as const;
export const waterUnitOptions = ['litres', 'ImperialGallons'] as const;

// Mock environments
export const mockEnvironments = [
  { id: 'env1', name: 'Production', type: 'PROD' },
  { id: 'env2', name: 'Staging', type: 'STAGE' },
  { id: 'env3', name: 'Development', type: 'DEV' },
];

// Step 1 Schema - Basic project information
// Note: country, languages, and timeZone fields are now optional as they are primarily
// handled by the config registry system. They can be populated from config registry
// defaults or user input, and duplicate configs are handled automatically.
export const createProjectPartOneSchema = z.object({
  environment_id: z.number().nullable().optional(), // For existing environment
  environment_name: z.string().optional(), // For new environment
  // New single environment field for future use
  environment: z.object({
    id: z.string().optional(),
    name: z.string()
      .min(1, "Environment name is required.")
      .max(50, "Environment name cannot exceed 50 characters")
      .regex(/^[a-zA-Z0-9-]+$/, "Environment name can only contain letters, numbers, and hyphens")
      .refine((val) => val.trim().length >= 2, {
        message: "Environment name must be at least 2 characters long"
      })
      .refine((val) => !val.startsWith('-') && !val.endsWith('-'), {
        message: "Environment name cannot start or end with a hyphen"
      })
      .refine((val) => !val.includes('--'), {
        message: "Environment name cannot contain consecutive hyphens"
      }),
  }).passthrough().optional(),
  projectType: z.enum(projectTypeValues, {
    required_error: "Please select a Project Type.",
  }),
  awsRegion: z.string(),
  country: z.string().optional(),
  stateCode: z.string()
    .min(1, "Please select a state/province")
    .optional(),
  projectId: z.string()
    .length(5, "Project ID must be exactly 5 digits")
    .regex(/^\d{5}$/, "Project ID must be a 5-digit number"),
  name: z.string()
    .min(3, "Project Name must be at least 3 characters")
    .max(100, "Project Name cannot exceed 100 characters")
    .regex(/^[a-z0-9-]+$/, "Project Name can only contain lowercase letters, numbers, and hyphens")
    .refine((val) => !val.startsWith('-') && !val.endsWith('-'), {
      message: "Project Name cannot start or end with a hyphen"
    })
    .refine((val) => !val.includes('--'), {
      message: "Project Name cannot contain consecutive hyphens"
    }),
  description: z.string()
    .max(500, "Project Description cannot exceed 500 characters")
    .optional(),
  projectImageUrl: z.string().url({ message: "Please enter a valid URL." }).optional().or(z.literal('')),
  languages: z.string().optional(),
  timeZone: z.string().optional(),
  utility: z.object({
    id: z.number(),
    name: z.string(),
  }, {
    required_error: "Please select a utility.",
  }),
  utility_id: z.number().optional(),
  // Gating questions asked up-front on step 1; used to filter the config registry fetch.
  customerSegment: z.enum(['RESIDENTIAL', 'SMB']).optional(),
  meterTypes: z.array(z.enum(['AMI', 'AMR', 'NSM'])).optional(),
  fuelTypes: z.array(z.enum(['ELECTRIC', 'GAS', 'WATER'])).optional(),
  // Ensure either environment_id or environment_name is provided
}).refine((data) => {
  return (data.environment_id && data.environment_id !=null ) || 
         (data.environment_name && data.environment_name.trim() !== '');
}, {
  message: "Either select an existing environment or provide a new environment name",
  path: ["environment_id"] // This will show the error on the environment_id field
});

export type CreateProjectPartOneValues = z.infer<typeof createProjectPartOneSchema>;

// Step 2 Schema - Project details and data source (Dynamic)
export const createProjectPartTwoSchema = z.record(z.any()).superRefine((data, ctx) => {
  // Basic validation for required fields
  if (data.dataSourceType === 'sftp') {
    if (!data.sftpUser || data.sftpUser.trim().length < 1) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "SFTP Username is required.",
        path: ["sftpUser"],
      });
    }
    if (!data.sftpPassword || data.sftpPassword.trim().length < 1) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "SFTP Password is required.",
        path: ["sftpPassword"],
      });
    }
    if (!data.sftpHostName || data.sftpHostName.trim().length < 1) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "SFTP Server Host Name is required.",
        path: ["sftpHostName"],
      });
    }
  }
});

export type CreateProjectPartTwoValues = z.infer<typeof createProjectPartTwoSchema>;

// Utility creation schema
export const createUtilityFormSchema = z.object({
  country: z.string().min(1, "Please select a country for the utility."),
  utilityName: z.string().min(1, "Utility name cannot be empty.").max(100, "Utility name is too long."),
  address: z.string().max(200, "Address is too long.").optional(),
  state: z.string().min(2, "State/Province must be at least 2 characters.").max(50, "State/Province is too long."),
  zipCode: z.string().min(1, "Zip/Postal code cannot be empty.").max(20, "Zip/Postal code is too long."),
});

export type CreateUtilityFormValues = z.infer<typeof createUtilityFormSchema>; 